import { useEffect, useState } from 'react';

import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Unstable_Grid2';
import Typography from '@mui/material/Typography';
import {
  alpha,
  Button,
  Link,
  ListItemText,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tabs,
} from '@mui/material';

import { RouterLink } from 'src/routes/components';

import { fDateTime } from 'src/utils/format-time';
import { hasPermission } from 'src/utils/has-permissions';
import { fCurrencyRupees } from 'src/utils/format-number';
import { TABLE_HEAD_CELL_TYPE } from 'src/utils/common-types';

import { useAuthContext } from 'src/auth/hooks';
import { AuthPermissions } from 'src/auth/permissions';
import { useAppDispatcher, useAppSelector } from 'src/redux/store';
import { getPaginatedAdvancedPayments } from 'src/redux/slices/advanced-payments';

import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import TableErrorData from 'src/components/table/table-error-data';
import {
  TableHeadCustom,
  TableNoData,
  TablePaginationCustom,
  TableSkeleton,
  useTable,
} from 'src/components/table';

import { IEmployeeItem } from 'src/types/employee';
import { IAdvancedPaymentListItem } from 'src/types/advanced-payment';

import { paths } from '../../routes/paths';

const LINE_ITEM_HEADERS: TABLE_HEAD_CELL_TYPE[] = [
  {
    id: 'date',
    label: 'Paid Date',
    align: 'left',
    primary: true,
  },
  { id: 'amount', label: 'Amount', align: 'right', primary: true },
  { id: 'updatedAt', label: 'Updated At', primary: true, align: 'left' },
];

const TABS = [
  { value: 'overview', label: 'Overview' },
  { value: 'advancedPayments', label: 'Advanced Payments History' },
];

type Props = {
  currentEmployee?: IEmployeeItem;
};

const defaultSortBy: { sortBy: string; order: 'asc' | 'desc' } = {
  sortBy: 'updatedAt',
  order: 'desc',
};

export default function EmployeeDetails({ currentEmployee }: Props) {
  const dispatch = useAppDispatcher();

  const { user: authUser } = useAuthContext();

  const { status, payments, error } = useAppSelector((state) => state.advancedPayments);

  const [tabValue, setTabValue] = useState('overview');

  const table = useTable({
    defaultRowsPerPage: 25,
  });

  const { page, rowsPerPage } = table;

  const [sort, setSort] = useState(defaultSortBy);

  useEffect(() => {
    if (tabValue === 'advancedPayments') {
      dispatch(
        getPaginatedAdvancedPayments({
          page,
          limit: rowsPerPage,
          sort: sort.order,
          sortBy: sort.sortBy,
          generalSearch: '',
          employee: currentEmployee?._id,
        })
      );
    }
  }, [tabValue, currentEmployee, dispatch, page, rowsPerPage, sort]);

  return (
    <Grid container spacing={3}>
      <Grid xs={12}>
        <Card sx={{ p: 3 }}>
          <Tabs
            value={tabValue}
            onChange={(_, value) => setTabValue(value)}
            sx={{
              px: 2.5,
              boxShadow: (theme) => `inset 0 -2px 0 0 ${alpha(theme.palette.grey[500], 0.08)}`,
            }}
          >
            {TABS.map((tab) => (
              <Tab key={tab.value} iconPosition="end" value={tab.value} label={tab.label} />
            ))}
          </Tabs>

          {tabValue === 'overview' && (
            <Grid container spacing={2} mt={2}>
              <Grid
                mb={1}
                display="flex"
                mt={2}
                alignItems="center"
                justifyContent="space-between"
                width={1}
              >
                <Typography variant="subtitle1">Basic Information</Typography>

                <Stack gap={2} direction="row" alignItems="center">
                  {hasPermission(
                    authUser,
                    'any',
                    AuthPermissions.UPDATE_EMPLOYEE,
                    AuthPermissions.ADMIN_PERMISSION,
                    AuthPermissions.SUPER_ADMIN_PERMISSION
                  ) && (
                    <Link
                      component={RouterLink}
                      href={paths.dashboard.employees.edit(currentEmployee?._id ?? '')}
                    >
                      <Button variant="text" startIcon={<Iconify icon="solar:pen-bold" />}>
                        Edit
                      </Button>
                    </Link>
                  )}
                </Stack>
              </Grid>

              <Grid xs={12} md={4}>
                <ListItemText
                  primary="Employee Name"
                  secondary={currentEmployee?.name}
                  primaryTypographyProps={{ typography: 'body2' }}
                  secondaryTypographyProps={{
                    component: 'span',
                    color: 'text.disabled',
                  }}
                />
              </Grid>

              <Grid xs={12} md={4}>
                <ListItemText
                  primary="Employee Number"
                  secondary={currentEmployee?.employeeNumber}
                  primaryTypographyProps={{ typography: 'body2' }}
                  secondaryTypographyProps={{
                    component: 'span',
                    color: 'text.disabled',
                  }}
                />
              </Grid>

              <Grid xs={12} md={4}>
                <ListItemText
                  primary="Base Salary"
                  secondary={fCurrencyRupees((currentEmployee?.baseSalary || 0) * 100)}
                  primaryTypographyProps={{ typography: 'body2' }}
                  secondaryTypographyProps={{
                    component: 'span',
                    color: 'text.disabled',
                  }}
                />
              </Grid>

              <Grid xs={12} md={4}>
                <ListItemText
                  primary="Deducting EPF"
                  secondary={currentEmployee?.epf ? 'Yes' : 'No'}
                  primaryTypographyProps={{ typography: 'body2' }}
                  secondaryTypographyProps={{
                    component: 'span',
                    color: 'text.disabled',
                  }}
                />
              </Grid>
            </Grid>
          )}

          {tabValue === 'advancedPayments' && (
            <Stack>
              <Typography variant="subtitle1" sx={{ mt: 2 }}>
                {currentEmployee?.name}
              </Typography>
              <TableContainer
                sx={{ position: 'relative', overflow: 'hidden', height: '400px', mt: 2 }}
              >
                <Scrollbar>
                  <Table stickyHeader size="medium" sx={{ minWidth: 960 }}>
                    <TableHeadCustom
                      headLabel={LINE_ITEM_HEADERS}
                      order={sort.order}
                      orderBy={sort.sortBy}
                      rowCount={payments.items.length}
                      onSort={(id) => {
                        if (id === sort.sortBy) {
                          setSort((prev) => ({
                            ...prev,
                            order: prev.order === 'asc' ? 'desc' : 'asc',
                          }));
                        } else {
                          setSort({
                            sortBy: id,
                            order: 'asc',
                          });
                        }
                      }}
                    />

                    <TableBody>
                      {status === 'loading' ? (
                        [...Array(10)].map((i, index) => <TableSkeleton key={index} />)
                      ) : (
                        <>
                          {payments?.items.map((row: IAdvancedPaymentListItem, index: number) => (
                            <TableRow hover key={row._id}>
                              <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
                                {fDateTime(row.paidDate)}
                              </TableCell>

                              <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                                {fCurrencyRupees(row.amountInCents)}
                              </TableCell>

                              <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
                                {fDateTime(row.updatedAt)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </>
                      )}

                      {status !== 'failure' && payments.count === 0 && (
                        <TableNoData message="No payment found" notFound={payments.count === 0} />
                      )}

                      {status === 'failure' && <TableErrorData notFound error={error} />}
                    </TableBody>
                  </Table>
                </Scrollbar>
              </TableContainer>
              <TablePaginationCustom
                count={payments.count}
                page={table.page}
                rowsPerPage={table.rowsPerPage}
                onPageChange={table.onChangePage}
                onRowsPerPageChange={table.onChangeRowsPerPage}
                dense={table.dense}
                onChangeDense={table.onChangeDense}
              />
            </Stack>
          )}

          <Stack direction="row" justifyContent="flex-end" gap={2} mt={2}>
            <Link component={RouterLink} href={paths.dashboard.employees.root}>
              <Button variant="outlined" color="inherit">
                Cancel
              </Button>
            </Link>

            {hasPermission(
              authUser,
              'any',
              AuthPermissions.CREATE_ADVANCED_PAYMENT,
              AuthPermissions.ADMIN_PERMISSION,
              AuthPermissions.SUPER_ADMIN_PERMISSION
            ) && (
              <Link
                component={RouterLink}
                href={paths.dashboard.advancedPayments.new(currentEmployee?._id ?? '')}
              >
                <Button variant="contained" color="inherit">
                  Make Payment
                </Button>
              </Link>
            )}
          </Stack>
        </Card>
      </Grid>
    </Grid>
  );
}
